<template>
    <section :class="classes">
        <markets-ui-pagination
            v-if="hasPagination('top')"
            :class="namespace('__pagination--top')"
            :skin="skin"
            :namespaced="namespaced"
            :perPage="computedOption.rowsPerPage"
            :total="cRows.length"
            :page="currentPage"
            @change="setCurrentPage"
        >
        </markets-ui-pagination>
        <markets-ui-pagination
            v-if="hasStickyPagination('top')"
            :class="namespace('__sticky-pagination--top')"
            :skin="skin"
            :namespaced="namespaced"
            :showFirstAndLast="false"
            :showIcon="false"
            :showPageNumbers="false"
            :previousLabel="computedLabels.previous"
            :previousAriaLabel="computedLabels.previousAria"
            :nextLabel="computedLabels.next"
            :nextAriaLabel="computedLabels.nextAria"
            :perPage="stickyState.columnsPerPage"
            :total="totalNonStickyColumns.length"
            :page="stickyState.currentPage"
            @change="setStickyCurrentPage"
        >
        </markets-ui-pagination>
        <sort-controller
            v-if="isMobileMode"
            :skin="skin"
            :namespaced="namespaced"
            :fields="sortFields"
            :orders="sortOrders"
            @change="sort"
        >
        </sort-controller>
        <table role="grid" :class="tableCls">
            <caption v-if="caption" :class="[mdsNS('data-table__caption')]">
                {{
                    caption
                }}
            </caption>
            <thead v-if="!isMobileMode" :class="headerCls">
                <tr :class="getRowClass()" role="row">
                    <th
                        scope="col"
                        v-for="column in computedColumns"
                        :key="column.id"
                        :class="getHeaderCellClass({ column })"
                        :aria-sort="getAriaSort(column)"
                    >
                        <div
                            v-if="column.id === 'expand'"
                            tabindex="0"
                            role="button"
                            :class="getHeaderCellInnerClass({ column })"
                            v-html="
                                getIconFormatValue(
                                    collapseAll
                                        ? '#triangle-fill-right'
                                        : '#triangle-fill-down'
                                )
                            "
                            @click="collapse()"
                            @keyup.enter="collapse()"
                        ></div>
                        <div
                            v-else
                            :role="column.sortable ? 'button' : ''"
                            tabindex="0"
                            :class="getHeaderCellInnerClass({ column })"
                            @click="sort(column)"
                            @keyup.enter="sort(column)"
                            :aria-label="column.name"
                        >
                            <markets-ui-checkbox
                                v-if="column.dataType === 'checkbox'"
                                :namespaced="namespaced"
                                :checked="false"
                                value="0"
                                @change="clickHeaderCheckbox"
                            >
                            </markets-ui-checkbox>
                            {{ column.name }}
                        </div>
                    </th>
                </tr>
            </thead>
            <tbody v-if="isMobileMode" :class="bodyerCls">
                <tr
                    v-for="item in columnsRows"
                    :key="item.id"
                    :class="getRowClass({ isHighlight: item.highlight })"
                    role="row"
                >
                    <td
                        role="gridcell"
                        v-if="item.highlight"
                        :class="getCellClass({ isHighlight: true })"
                        :colspan="Math.max(item.data.length, 2)"
                    >
                        <div :class="namespace('__cell--highlight-wrapper')">
                            <div
                                v-for="d in item.data"
                                :class="getCellInnerClass({ column: d.column })"
                                :key="d.row.id + '_' + d.column.id"
                                @click="clickRow(d.row, $event)"
                                v-html="
                                    d.column.formatter(
                                        d.row[d.column.id],
                                        d.row,
                                        d.column
                                    )
                                "
                            ></div>
                        </div>
                    </td>
                    <template v-else>
                        <td role="gridcell" :class="getCellClass()">
                            {{ item.data[0].column.name }}
                        </td>
                        <td
                            role="gridcell"
                            :ref="
                                getCellRefId(
                                    item.data[0].row,
                                    item.data[0].column
                                )
                            "
                            :class="
                                getCellClass({
                                    column: {
                                        align: 'right',
                                        id: item.data[0].column.id
                                    }
                                })
                            "
                        >
                            <div
                                :class="
                                    getCellInnerClass({
                                        column: item.data[0].column
                                    })
                                "
                                v-html="
                                    item.data[0].column.formatter(
                                        item.data[0].row[
                                            item.data[0].column.id
                                        ],
                                        item.data[0].row,
                                        item.data[0].column
                                    )
                                "
                            ></div>
                        </td>
                    </template>
                </tr>
            </tbody>
            <tbody v-else :class="bodyerCls">
                <tr
                    v-for="row in computedRows"
                    :key="row.id"
                    :class="getRowClass({ row })"
                    role="row"
                    @click="clickRow(row, $event)"
                    @dblclick="dbclickRow(row, $event)"
                    @mouseenter="
                        $emit('row-mouse-enter', {
                            row,
                            e: $event
                        })
                    "
                    @mouseleave="
                        $emit('row-mouse-leave', {
                            row,
                            e: $event
                        })
                    "
                >
                    <template v-if="row.isGroupRow">
                        <td :class="getCellClass()" role="gridcell">
                            <div
                                v-html="
                                    getIconFormatValue(
                                        isCollapse(row)
                                            ? '#triangle-fill-right'
                                            : '#triangle-fill-down'
                                    )
                                "
                                @click="collapse(row)"
                            ></div>
                        </td>
                        <td
                            :colspan="computedColumns.length - 1"
                            :class="getCellClass()"
                            role="gridcell"
                        >
                            {{ row.group }}
                        </td>
                    </template>
                    <template v-else>
                        <td
                            v-for="column in computedColumns"
                            :key="column.id"
                            :ref="getCellRefId(row, column)"
                            :class="getCellClass({ column })"
                            :data-column-id="column.id"
                            role="gridcell"
                            @mouseenter="
                                $emit('cell-mouse-enter', {
                                    row,
                                    column,
                                    e: $event
                                })
                            "
                            @mouseleave="
                                $emit('cell-mouse-leave', {
                                    row,
                                    column,
                                    e: $event
                                })
                            "
                        >
                            <div
                                :class="getCellInnerClass({ column })"
                                v-html="
                                    column.formatter(
                                        row[column.id],
                                        row,
                                        column
                                    )
                                "
                            ></div>
                        </td>
                    </template>
                </tr>
            </tbody>
        </table>
        <markets-ui-pagination
            v-if="hasPagination('bottom')"
            :class="namespace('__pagination--bottom')"
            :skin="skin"
            :namespaced="namespaced"
            :perPage="computedOption.rowsPerPage"
            :total="cRows.length"
            :page="currentPage"
            @change="setCurrentPage"
        >
        </markets-ui-pagination>
        <markets-ui-pagination
            v-if="hasStickyPagination('bottom')"
            :class="namespace('__sticky-pagination--bottom')"
            :skin="skin"
            :namespaced="namespaced"
            :showFirstAndLast="false"
            :showIcon="false"
            :showPageNumbers="false"
            :previousLabel="computedLabels.previous"
            :previousAriaLabel="computedLabels.previousAria"
            :nextLabel="computedLabels.next"
            :nextAriaLabel="computedLabels.nextAria"
            :perPage="stickyState.columnsPerPage"
            :total="totalNonStickyColumns.length"
            :page="stickyState.currentPage"
            @change="setStickyCurrentPage"
        >
        </markets-ui-pagination>
    </section>
</template>

<script>
import utils from '../../utils';
import mixins from '../mixins';
import baseMixin from './base-mixin';
import SortController from './sort-controller';
import { getIconFormatValue, getDataFormatter } from './data-formatter';
const CLASSES = {
    VISIBLE_ROWS: 'visible-rows-update',
    BUTTON_ONLY: '__button--icon-only'
};
export default {
    name: 'markets-ui-table',
    mixins: [mixins, baseMixin],
    components: { SortController },
    data() {
        return {
            cRows: [], // clone the props rows to avoid modify outside data
            sortColumns: {},
            currentSortColumnId: '',
            width: 0,
            currentPage: 1,
            stickyState: {
                stickyMode: false,
                columnsPerPage: 0,
                stickyColIndices: [],
                currentPage: 1
            },
            collapseAll: false,
            collapseGroups: {}
        };
    },
    computed: {
        classes() {
            const cls = [this.namespace(), this.skinStyle];
            if (this.skin) {
                cls.push(
                    `${this.namespace()}__${utils.getSkinSuffix(this.skin)}`
                );
            }
            return cls;
        },
        selectable() {
            // selection checkbox is usually the first column
            return (
                this.computedOption.selectable ||
                this.columns[0].id === 'checkbox'
            );
        },
        sortFields() {
            const fields = [];
            this.computedColumns.forEach(col => {
                if (col.sortable) {
                    fields.push({
                        id: col.id,
                        name: col.name,
                        selected: col.id === this.currentSortColumnId
                    });
                }
            });
            return fields;
        },
        sortOrders() {
            return ['ascending', 'descending'].map(id => {
                return {
                    id: id,
                    name: this.computedLabels[id],
                    disabled: false,
                    selected: this.currentSortColumnId
                        ? this.sortColumns[this.currentSortColumnId] === id
                        : false
                };
            });
        },
        highlightColumns() {
            let cols;
            if (this.computedOption.highlightColumns) {
                cols = this.computedColumns.highlightColumns.filter(
                    col =>
                        utils.inArray(
                            this.computedColumns,
                            c => c.id === col.id
                        ) > -1
                );
            }
            if (!cols) {
                cols = [this.computedColumns[0]];
            }
            return cols;
        },
        isMobileMode() {
            return this.computedOption.mobileBreakPoint <= this.width;
        },
        hasPagination() {
            return mode => {
                const { paginationMode } = this.computedOption;
                return paginationMode === 'both' || paginationMode === mode;
            };
        },
        hasStickyPagination() {
            return stickyPaginationMode => {
                return (
                    this.stickyState.stickyMode &&
                    this.computedOption.stickyPaginationMode ===
                        stickyPaginationMode
                );
            };
        },
        tableCls() {
            return `${this.mdsNS(
                'data-table data-table--small'
            )} ${this.namespace()}`;
        },
        headerCls() {
            return [this.mdsNS('data-table__header'), this.namespace('thead')];
        },
        bodyerCls() {
            return [this.mdsNS('data-table__body'), this.namespace('tbody')];
        },
        computedLabels() {
            return utils.extend(
                {
                    previous: 'Previous columns',
                    previousAria: 'Go to previous columns',
                    next: 'Next columns',
                    nextAria: 'Go to next columns',
                    selectSortField: 'Select sort field',
                    selectSortOrder: 'Select sort order',
                    ascending: 'Asc',
                    descending: 'Desc'
                },
                this.labels
            );
        },
        computedOption() {
            return utils.extend(
                true,
                {
                    removable: false,
                    focusable: false,
                    selectable: true,
                    rowsPerPage: -1,
                    paginationMode: 'bottom',
                    currentPage: 1,
                    highlightColumns: [],
                    mobileBreakPoint: Number.MAX_SAFE_INTEGER,
                    stickyPaginationMode: 'top', //'top', 'bottom'
                    stickyCurrentPage: 1,
                    stickyLeadBreakPoints: []
                },
                this.option
            );
        },
        visibleColumnsMap() {
            const map = {};
            this.computedColumns.forEach(col => {
                map[col.id] = col;
            });
            return map;
        },
        computedColumns() {
            let cols = [];
            const {
                stickyMode,
                stickyColIndices,
                columnsPerPage,
                currentPage
            } = this.stickyState;
            if (stickyMode) {
                const visibleCols = this.totalNonStickyColumns.filter(
                    (col, i) =>
                        i >= (currentPage - 1) * columnsPerPage &&
                        i < currentPage * columnsPerPage
                );
                cols = this.columns.filter(
                    (col, i) =>
                        utils.findIndex(stickyColIndices, i) > -1 ||
                        utils.findIndex(visibleCols, c => c.id === col.id) > -1
                );
            } else {
                cols = this.columns;
            }
            cols = cols.map(col => this.getNewCol(col));
            const { removable } = this.computedOption;
            if (removable) {
                cols.push({
                    id: 'remove',
                    name: '',
                    dataType: 'icon',
                    align: 'center',
                    width: 50,
                    formatter: () => {
                        return getIconFormatValue({
                            namespaced: this.namespaced,
                            cls: `${this.namespace(
                                CLASSES.BUTTON_ONLY
                            )} ${this.namespace(
                                '__button--small'
                            )} ${this.namespace('__button--icon-only-remove')}`,
                            svgCls: this.namespace('__icon')
                        })('#remove--s');
                    },
                    sortable: false
                });
            }
            if (this.groupKey) {
                cols.unshift({
                    id: 'expand',
                    name: '',
                    dataType: 'icon',
                    width: 15,
                    formatter: () => {
                        return '';
                    },
                    sortable: false
                });
            }
            return cols;
        },
        flattenRows() {
            const { rowsPerPage } = this.computedOption;
            const id = this.currentSortColumnId;
            const reverse =
                this.sortColumns[id] === 'descending' ? false : true;
            let rows = utils.orderBy(this.cRows, id, reverse);
            if (rowsPerPage > 0) {
                rows = rows.filter(
                    (row, i) =>
                        i >= (this.currentPage - 1) * rowsPerPage &&
                        i < this.currentPage * rowsPerPage
                );
            }
            return rows;
        },
        visibleGroupRowKeys() {
            if (this.groupKey) {
                return this.computedRows
                    .filter(row => row.isGroupRow)
                    .map(row => row.group);
            } else {
                return [];
            }
        },
        groupRows() {
            if (this.groupKey && this.cRows) {
                return this.cRows.reduce((res, row) => {
                    const group = row[this.groupKey] || 'Undefined';
                    res[group] = res[group] || [];
                    res[group].push(row);
                    return res;
                }, {});
            } else {
                return {};
            }
        },
        computedRows() {
            const { rowsPerPage } = this.computedOption;
            if (!this.groupKey) {
                return this.flattenRows;
            }
            const id = this.currentSortColumnId;
            const reverse =
                this.sortColumns[id] === 'descending' ? false : true;
            let c = 0;
            const keys = Object.keys(this.groupRows);
            const rowsList = [];
            for (let i = 0, l = keys.length, group; i < l; i++) {
                const rows = [];
                group = keys[i];
                const orderRows = utils.orderBy(
                    this.groupRows[group],
                    id,
                    reverse
                );
                let needGroup = false;
                for (let j = 0, jl = orderRows.length; j < jl; j++) {
                    if (
                        rowsPerPage < 0 ||
                        (c >= (this.currentPage - 1) * rowsPerPage &&
                            c < this.currentPage * rowsPerPage)
                    ) {
                        needGroup = true;
                        rows.push(orderRows[j]);
                    }
                    c++;
                }
                if (needGroup) {
                    rows.unshift({
                        isGroupRow: true,
                        group
                    });
                    rowsList.push(rows);
                }
                if (rowsPerPage > 0 && c >= this.currentPage * rowsPerPage) {
                    break;
                }
            }
            return rowsList.flat();
        },
        columnsRows() {
            if (!this.isMobileMode) {
                return [];
            }
            const columnsRows = [];
            const { highlightColumns } = this.computedOption;
            this.computedRows.forEach(row => {
                const highlightRow = [];
                const colRows = [];
                this.computedColumns.forEach(column => {
                    if (
                        utils.inArray(highlightColumns, column.id) > -1 ||
                        column.id === 'remove'
                    ) {
                        highlightRow.push({
                            row,
                            column
                        });
                    } else {
                        colRows.push({
                            id: `${row.id}_${column.id}`,
                            data: [
                                {
                                    column,
                                    row
                                }
                            ],
                            highlight: false
                        });
                    }
                });
                if (highlightRow.length) {
                    colRows.unshift({
                        id: `${row.id}_${highlightRow
                            .map(item => item.column.id)
                            .join('_')}`,
                        data: highlightRow,
                        highlight: true
                    });
                }
                columnsRows.push(colRows);
            });
            return columnsRows.flat();
        },
        totalNonStickyColumns() {
            const { stickyColIndices } = this.stickyState;

            // remove column need to be sticky automaticlly
            let removeIdx = -1;
            if (this.computedOption.removable) {
                removeIdx = utils.findIndex(
                    this.columns,
                    col => col.id === 'remove'
                );
            }

            return this.columns.filter(
                (col, index) =>
                    removeIdx === index ||
                    utils.findIndex(stickyColIndices, index) === -1
            );
        },
        hasCheckboxColumn() {
            return !!utils.find(this.columns, col => col.id === 'checkbox');
        },
        hasFlashColumn() {
            return !!utils.find(this.computedColumns, col => col.flash);
        }
    },
    watch: {
        rows: {
            handler(value) {
                const oldValue = [...this.cRows];
                const oldVisibleRows = this.getVisibleRows();
                this.cRows = value.map(row => this.checkboxHandler(row));
                let flag = false;
                // check if there is column need flash, if not, no need to flash cell to improve performance
                if (
                    this.same(this.cRows, oldValue, ['id', this.groupKey]) &&
                    this.hasFlashColumn
                ) {
                    this.flashCells(value, oldValue);
                } else {
                    flag = true;
                }
                if (flag) {
                    const newRowsMap = this.getRowsMap(value);
                    const visibleRows = this.getVisibleRows();
                    const visibleRowsMap = this.getRowsMap(visibleRows);
                    const oldVisibleRowsMap = this.getRowsMap(oldVisibleRows);

                    const addedRows = [];
                    const hidedRows = [];
                    const removedRows = [];
                    // get removed/hided rows
                    oldVisibleRows.forEach(row => {
                        if (!newRowsMap[row.id]) {
                            removedRows.push(row);
                        } else if (!visibleRowsMap[row.id]) {
                            hidedRows.push(row);
                        }
                    });
                    // get added rows
                    visibleRows.forEach(row => {
                        if (!oldVisibleRowsMap[row.id]) {
                            addedRows.push(row);
                        }
                    });

                    if (
                        addedRows.length ||
                        removedRows.length ||
                        hidedRows.length
                    ) {
                        this.$emit(CLASSES.VISIBLE_ROWS, {
                            visibleRows,
                            addedRows,
                            removedRows,
                            hidedRows
                        });
                    }
                }
            },
            deep: true,
            immediate: true
        },
        visibleGroupRowKeys: {
            handler(value) {
                if (value && value.length) {
                    value.forEach(group => {
                        const status =
                            this.collapseAll ||
                            this.collapseGroups[group] ||
                            false;
                        this.$set(this.collapseGroups, group, status);
                    });
                }
            },
            immediate: true
        },
        selectRowIds: {
            handler(value) {
                if (value && value.length) {
                    value.forEach(id => {
                        this.$set(this.selectedRows, id, true);
                    });
                }
            },
            immediate: true
        },
        selectedRows: {
            handler() {
                this.setHeaderCheckboxStatus();
                this.$emit(
                    'rows-selected-changed',
                    this.cRows
                        .filter(row => this.selectedRows[row.id])
                        .map(row => {
                            row.checkbox = true;
                            return row;
                        })
                );
            },
            deep: true
        },
        focusRowId(value) {
            this.focusRow = this.cRows.find(row => row.id === value);
        },
        sortColumn: {
            handler(value) {
                this.setSortColumn(value);
            },
            deep: true
        }
    },
    created() {
        this.namespace = utils.namespace('ui-table', false);
        this.getIconFormatValue = getIconFormatValue({
            namespaced: this.namespaced,
            cls: `${this.namespace(CLASSES.BUTTON_ONLY)} ${this.namespace(
                '__button--small'
            )}`,
            svgCls: this.namespace('__icon')
        });
        this.setSortColumn(this.sortColumn);
        this.currentPage = this.computedOption.currentPage;
        this.throttleResize = utils.throttle(size => {
            this.calculateStickyState();
        }, 200);
        this.triggerClick = utils.debounce((row, e) => {
            if (this.computedOption.focusable) {
                this.focusRow = row;
            }
            if (this.hasCheckboxColumn) {
                row.checkbox = !!this.selectedRows[row.id];
            }
            this.$emit('row-click', { row, e });
        }, 100);
    },
    mounted() {
        this.calculateStickyState();
        utils.resizeObserver.observe(this.$el, this.throttleResize);
        this.setHeaderCheckboxStatus();
        this.$emit('render-complete');
    },
    beforeDestroy() {
        utils.resizeObserver.unobserve(this.$el, this.throttleResize);
    },
    methods: {
        getAriaSort(column) {
            return this.sortColumns[column.id] || 'none';
        },
        getRowClass({ isHighlight = false, row = {} } = {}) {
            const cls = [
                this.mdsNS('data-table__row'),
                this.namespace('__row')
            ];
            if (isHighlight) {
                cls.push(this.namespace('__row--highlight'));
            }
            const { isGroupRow } = row;
            const { selectable, focusable } = this.computedOption;
            if (isGroupRow) {
                cls.push(this.namespace('__row--group'));
            } else {
                if (this.groupKey && row[this.groupKey]) {
                    const visible = this.collapseGroups[row[this.groupKey]]
                        ? 'hide'
                        : 'show';
                    cls.push(this.namespace(`__row--${visible}`));
                }
            }
            if (selectable && this.selectedRows[row.id]) {
                cls.push(this.namespace('__row--selected'));
            }
            if (focusable && this.focusRow && this.focusRow.id === row.id) {
                cls.push(this.namespace('__row--focus'));
            }
            return cls;
        },
        getNewCol(col) {
            const newCol = utils.extend(
                {},
                {
                    sortable: true
                },
                col
            );
            if (newCol.dataType === 'checkbox') {
                newCol.sortable = false;
            } else if (newCol.dataType === 'tree') {
                newCol.dataType = this.computedHover.menu.length
                    ? newCol.dataType
                    : 'string';
            }
            if (newCol.formatter && typeof newCol.formatter !== 'function') {
                newCol.dataType = newCol.formatter.replace(/(.+)Format/, '$1');
            }
            if (!newCol.formatter || typeof newCol.formatter !== 'function') {
                newCol.formatter = getDataFormatter({
                    formatter: this.formatter,
                    dataType: newCol.dataType,
                    namespaced: this.namespaced,
                    namespace: this.namespace,
                    skin: this.skin
                });
            } else if (
                typeof newCol.formatter === 'function' &&
                newCol.dataType === 'tree'
            ) {
                const originalFormatter = newCol.formatter;
                newCol.formatter = (value, d) => {
                    return getDataFormatter({
                        formatter: this.formatter,
                        dataType: newCol.dataType,
                        namespaced: this.namespaced,
                        namespace: this.namespace,
                        skin: this.skin
                    })(originalFormatter(value, d));
                };
            }
            return newCol;
        },
        flashCells(value, oldValue) {
            const visibleColumnsMap = this.visibleColumnsMap || {};
            const visibleRowsMap = this.getRowsMap(this.computedRows);
            this.cRows.forEach((row, i) => {
                if (row && visibleRowsMap[row.id]) {
                    for (const columnId in row) {
                        const column = visibleColumnsMap[columnId];
                        if (
                            column &&
                            column.flash &&
                            row[columnId] !== oldValue[i][columnId]
                        ) {
                            const cellNode = this.$refs[
                                this.getCellRefId(row, column)
                            ];
                            const flashClass = this.getFlashClass(
                                column,
                                row[columnId],
                                oldValue[i][columnId]
                            );
                            if (cellNode[0] && flashClass) {
                                utils.flash(cellNode[0], flashClass);
                            }
                        }
                    }
                }
            });
        },
        checkboxHandler(row) {
            const cRow = utils.extend(true, {}, row);
            if (this.hasCheckboxColumn) {
                if (utils.inArray(this.selectRowIds, row.id) > -1) {
                    cRow.checkbox = true;
                } else {
                    cRow.checkbox = false;
                }
            }
            return cRow;
        },
        getHeaderCellClass({ column = {} } = {}) {
            const cls = [
                this.mdsNS('data-table__header-cell'),
                this.namespace('__header-cell')
            ];
            if (column.headerClass) {
                cls.push(column.headerClass);
            }
            return cls;
        },
        getHeaderCellInnerClass({ column = {} } = {}) {
            const sort = this.getAriaSort(column);
            const cls = [
                this.mdsNS('data-table__header-cell-inner'),
                this.namespace('__header-cell-inner')
            ];
            if (column.sortable) {
                cls.push(
                    this.mdsNS(
                        `data-table__header-cell-inner--selected-${sort}`
                    )
                );
            }
            if (column.align) {
                cls.push(
                    this.mdsNS(`data-table__header-cell-inner--${column.align}`)
                );
                cls.push(
                    this.namespace(`__header-cell-inner--${column.align}`)
                );
            }
            cls.push(this.namespace(`__header-cell-inner--selected-${sort}`));
            return cls;
        },
        getCellClass({ isHighlight = false, column = {} } = {}) {
            const cls = [this.mdsNS('data-table__cell')];
            if (column.align) {
                cls.push(
                    this.mdsNS(`data-table__cell--${column.align}`),
                    this.namespace(`__cell--${column.align}`)
                );
            }
            cls.push(
                this.namespace('__cell'),
                this.namespace(`__cell-${column.dataType || 'string'}`)
            );
            if (column.id) {
                cls.push(this.namespace(`__cell-${column.id}`));
            }
            if (isHighlight) {
                cls.push(this.namespace('__cell--highlight'));
            }
            if (column.cellClass) {
                cls.push(column.cellClass);
            }
            return cls;
        },
        getCellInnerClass({ column = {} } = {}) {
            return [this.namespace('__cell-inner')];
        },
        setCurrentPage(para) {
            const oldVisibleRows = this.getVisibleRows();
            this.currentPage = para.page;
            this.setCheckboxStatus();
            this.triggerRowsUpdate(oldVisibleRows);
        },
        setStickyCurrentPage(para) {
            this.stickyState.currentPage = para.page;
        },
        calculateStickyState() {
            const { width } = utils.innerSizes(this.$el);
            this.width = width;
            if (this.isMobileMode) {
                this.stickyState.stickyMode = false;
            } else {
                const breakPoint = utils.find(
                    this.computedOption.stickyLeadBreakPoints,
                    item =>
                        this.width >= item.minWidth &&
                        this.width <= item.maxWidth
                );
                if (breakPoint) {
                    this.stickyState.stickyMode = true;
                    this.stickyState.stickyColIndices =
                        breakPoint.stickyColIndices;
                    this.stickyState.columnsPerPage = breakPoint.columnsPerPage;
                } else {
                    this.stickyState.stickyMode = false;
                }
                if (this.stickyState.stickyMode) {
                    if (!utils.isNumeric(this.stickyState.currentPage)) {
                        this.stickyState.currentPage =
                            this.computedOption.stickyCurrentPage || 1;
                    }
                }
            }
        },
        sort(column, order) {
            if (column.sortable) {
                const id = column.id;
                if (!order) {
                    order =
                        !this.sortColumns[id] ||
                        this.sortColumns[id] === 'none' ||
                        this.sortColumns[id] === 'descending'
                            ? 'ascending'
                            : 'descending';
                } else {
                    order = order.id;
                }
                this.$set(this.sortColumns, id, order);
                Object.keys(this.sortColumns).forEach(key => {
                    if (key !== id) {
                        this.$set(this.sortColumns, key, 'none');
                    }
                });
                this.currentSortColumnId = id;
            }
        },
        collapse(row) {
            if (!row) {
                this.collapseAll = !this.collapseAll;
                this.visibleGroupRowKeys.forEach(group => {
                    this.collapseGroups[group] = this.collapseAll;
                });
            } else {
                const { group } = row;
                const collapse = this.collapseGroups[group];
                this.collapseGroups[group] = !collapse;
                this.collapseAll =
                    this.visibleGroupRowKeys.filter(
                        group => !this.collapseGroups[group]
                    ).length === 0;
            }
        },
        isCollapse(row) {
            return this.collapseAll || this.collapseGroups[row.group];
        },
        clickRow(row, e) {
            const target = e.target;
            const classList = target.classList;
            if (
                classList.contains(this.namespace('__button--icon-only-remove'))
            ) {
                this.removeRows(row);
            } else if (classList.contains('markets-ui-checkbox-input')) {
                this.clickCheckbox(row, target);
            } else if (
                classList.contains(this.namespace(CLASSES.BUTTON_ONLY))
            ) {
                this.$emit('icon-click', {
                    element: target,
                    row,
                    e
                });
            }

            const cellNode = utils.closest(target, this.namespace('__cell'));
            if (cellNode) {
                const columnId = cellNode.getAttribute('data-column-id');
                const column = utils.find(
                    this.computedColumns,
                    col => col.id === columnId
                );
                this.$emit('cell-click', {
                    e,
                    row,
                    column
                });
            }
            this.triggerClick(row, e);
        },
        dbclickRow(row, e) {
            this.$emit('row-double-click', { row, e });
        },
        removeRows(row) {
            const index = this.cRows.findIndex(r => r.id === row.id);
            if (index > -1) {
                const oldVisibleRows = this.getVisibleRows();
                this.cRows.splice(index, 1);
                const visibleRows = this.getVisibleRows();
                const addedRows = visibleRows.filter(
                    r =>
                        utils.inArray(
                            oldVisibleRows,
                            row => row.id === r.id
                        ) === -1
                );

                if (this.selectedRows[row.id]) {
                    this.selectedRows[row.id] = null;
                    delete this.selectedRows[row.id];
                }
                this.$nextTick(() => {
                    this.$emit(CLASSES.VISIBLE_ROWS, {
                        visibleRows: visibleRows,
                        addedRows,
                        removedRows: [row],
                        hidedRows: []
                    });
                });
            }
        },
        clickHeaderCheckbox(d) {
            this.clickCheckbox(null, d, 'all');
        },
        clickCheckbox(row, checkbox, type) {
            if (checkbox.checked) {
                if (type === 'all') {
                    this.cRows.forEach(row => {
                        this.setSelectedRows(row);
                    });
                    this.setCheckboxStatus();
                } else {
                    this.setSelectedRows(row);
                }
            } else {
                if (type === 'all') {
                    this.selectedRows = {};
                    this.setCheckboxStatus();
                } else {
                    this.selectedRows[row.id] = null;
                    delete this.selectedRows[row.id];
                }
            }
        },
        setSelectedRows(row) {
            if (this.selectable) {
                this.$set(this.selectedRows, row.id, true);
            } else {
                this.selectedRows = {
                    [row.id]: true
                };
            }
        },
        setCheckboxStatus() {
            this.$nextTick(() => {
                const allCheckbox = this.$el.querySelectorAll(
                    `.${this.namespace('__cell')} .markets-ui-checkbox-input`
                );
                if (allCheckbox && allCheckbox.length) {
                    for (let i = 0; i < allCheckbox.length; i++) {
                        allCheckbox[i].checked = !!this.selectedRows[
                            allCheckbox[i].value
                        ];
                    }
                }
            });
        },
        setHeaderCheckboxStatus() {
            const headerCheckbox = this.$el.querySelector(
                `.${this.namespace('__header-cell')} .markets-ui-checkbox-input`
            );
            if (headerCheckbox) {
                const selectedCount = Object.keys(this.selectedRows).length;
                if (selectedCount === 0) {
                    headerCheckbox.checked = false;
                    headerCheckbox.indeterminate = false;
                } else {
                    const rowsCount = this.cRows.length;
                    if (selectedCount === rowsCount) {
                        headerCheckbox.checked = true;
                        headerCheckbox.indeterminate = false;
                    } else {
                        headerCheckbox.checked = false;
                        headerCheckbox.indeterminate = true;
                    }
                }
            }
        },
        getVisibleRows() {
            return this.computedRows.filter(row => !row.isGroupRow);
        },
        triggerRowsUpdate(oldVisibleRows = []) {
            const visibleRows = this.getVisibleRows();
            this.$nextTick(() => {
                this.$emit(CLASSES.VISIBLE_ROWS, {
                    visibleRows,
                    addedRows: visibleRows,
                    removedRows: [],
                    hidedRows: oldVisibleRows
                });
            });
        },
        getCellRefId(row, column) {
            return `${row.id}_${column.id}`;
        },
        getRowsMap(rows = []) {
            const map = {};
            rows.forEach(row => {
                if (!row.isGroupRow) {
                    map[row.id] = true;
                }
            });
            return map;
        },
        setSortColumn(value) {
            if (value) {
                const column = utils.find(
                    this.computedColumns,
                    col => col.id === value.id
                );
                if (column && value.order) {
                    this.$set(this.sortColumns, column.id, value.order);
                    this.currentSortColumnId = column.id;
                }
            }
        }
    }
};
</script>
<style lang="scss">
$namespace: 'markets-ui-table';
@import '../../mds/constants';
@import 'common';
.#{$namespace} {
    padding: 4px;
    & .markets-ui-checkbox {
        display: flex;
    }
    .markets-ui-checkbox {
        margin: 0;
    }
    &__sticky-pagination {
        &--top {
            margin-bottom: 24px;
            text-align: right;
        }
        &--bottom {
            margin-top: 24px;
            text-align: right;
        }
    }
    &__pagination {
        &--top {
            margin-bottom: 12px;
            text-align: center;
        }
        &--bottom {
            margin-top: 12px;
            text-align: center;
        }
    }
    &__row {
        &--group {
            td {
                font-weight: bold;
            }
        }
        &--show {
            display: table-row;
        }
        &--hide {
            display: none;
        }
        &--selected {
            .#{$namespace}__cell {
                @include hover;
                &--flash {
                    &-positive {
                        @include flash(positive);
                    }
                    &-negative {
                        @include flash(negative);
                    }
                }
            }
        }
        &--focus {
            .#{$namespace}__cell {
                border-bottom: 1px solid $mds-interactive-color-primary-hover;
            }
        }
        &:hover {
            .#{$namespace} {
                &__button--icon-only {
                    &-hover {
                        visibility: visible;
                    }
                }
                &__cell {
                    @include hover;
                    &--flash {
                        &-positive {
                            @include flash(positive);
                        }
                        &-negative {
                            @include flash(negative);
                        }
                    }
                }
            }
        }
    }
    &__header-cell {
        padding-top: 8px;
        &-inner--center {
            @include center;
        }
        &-inner--selected-none:after {
            height: 4px;
        }
    }

    &__cell {
        vertical-align: middle;
        &-expand {
            .#{$namespace}__header-cell-inner {
                height: 28px;
            }
        }
        &--highlight {
            &-wrapper {
                @include flexWrapper;
            }
        }
        &--highlight,
        &-tree {
            .#{$namespace}__cell-inner {
                @include flexWrapper;
            }
        }
        &--center {
            .#{$namespace}__cell-inner {
                @include center;
            }
        }
        &--highlight {
            background: #e5e5e5;
            font-weight: bold;
        }
        &-inner {
            position: relative;
        }
        &--flash {
            opacity: 0.6;
            &-positive {
                @include flash(positive);
            }
            &-negative {
                @include flash(negative);
            }
        }
    }
    &__button--icon-only {
        cursor: pointer;
        display: inline-block;
        &-hover {
            visibility: hidden;
        }
    }
    &__text-hover {
        margin-right: 5px;
    }
    &__icon--search {
        width: $mds-size-icon-s;
        height: $mds-size-icon-s;
        fill: $mds-color-neutral-12;
        margin: 0;
        padding: 0;
        position: absolute;
        top: 50%;
        right: 2px;
        -webkit-transform: translateY(-50%);
        transform: translateY(-50%);
    }
    &__input {
        border: none;
        height: 22px;
        font-size: 14px;
        border-bottom: 1px solid $mds-color-neutral-95;
        background: $mds-color-neutral-90;
        padding-right: 20px;
    }

    &__negative,
    &__icon.#{$namespace}__negative {
        @include performance(negative);
    }
    &__neutral,
    &__icon.#{$namespace}__neutral {
        @include performance();
    }
    &__positive,
    &__icon.#{$namespace}__positive {
        @include performance(positive);
    }

    &__dark-gray {
        .#{$namespace} {
            &__row {
                &--selected {
                    .#{$namespace}__cell {
                        @include dark-hover;
                        &--flash {
                            &-positive {
                                @include flash(positive, dark);
                            }
                            &-negative {
                                @include flash(negative, dark);
                            }
                        }
                    }
                }
                &:hover {
                    .#{$namespace} {
                        &__cell {
                            @include dark-hover;
                            &--flash {
                                &-positive {
                                    @include flash(positive, dark);
                                }
                                &-negative {
                                    @include flash(negative, dark);
                                }
                            }
                        }
                    }
                }
            }
            &__cell {
                &--highlight {
                    @include dark-hover;
                }
                &--flash {
                    &-positive {
                        @include flash(positive, dark);
                    }
                    &-negative {
                        @include flash(negative, dark);
                    }
                }
            }

            &__icon--search {
                fill: $mds-color-neutral-95;
            }
            &__negative,
            .#{$namespace}__negative {
                @include performance(negative, dark);
            }
            &__neutral,
            .#{$namespace}__neutral {
                @include performance(neutral, dark);
            }
            &__positive,
            &__icon.#{$namespace}__positive {
                @include performance(positive, dark);
            }
        }
    }
}
</style>
